import axios, { type AxiosResponse, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/store/auth'
import router from '@/router'
import { useSnackbarStore } from '@/store/snackbar'

export const API_PREFIX = import.meta.env.VITE_API_PREFIX || '/api/v1'

// Create centralized axios instance
const http = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
http.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Token refresh scaffolding
let isRefreshing = false
let refreshQueue: Array<(token: string | null) => void> = []

function onRefreshed(newToken: string | null) {
  refreshQueue.forEach((cb) => cb(newToken))
  refreshQueue = []
}

// Separate axios instance to avoid interceptor loops during refresh
const refreshHttp = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
})

// Response interceptor for global error handling
http.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError) => {
    const snackbar = useSnackbarStore()
    const status = error.response?.status
    const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean })

    // Try token refresh once on 401 if enabled
    if (
      status === 401 &&
      import.meta.env.VITE_AUTH_REFRESH_ENABLED === 'true' &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true

      if (!isRefreshing) {
        isRefreshing = true
        try {
          const refreshToken = localStorage.getItem('refresh_token')
          const resp = await refreshHttp.post(`${API_PREFIX}/auth/refresh`, { refresh_token: refreshToken })
          const newToken = (resp.data?.access_token as string) || null
          if (newToken) {
            localStorage.setItem('token', newToken)
          } else {
            localStorage.removeItem('token')
          }
          onRefreshed(newToken)
          isRefreshing = false
          // retry original request with new token
          originalRequest.headers = originalRequest.headers || {}
          if (newToken) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`
          }
          return http(originalRequest)
        } catch (refreshErr) {
          isRefreshing = false
          onRefreshed(null)
          // fall through to logout below
        }
      }

      // queue the request until refresh finishes
      return new Promise((resolve, reject) => {
        refreshQueue.push((newToken) => {
          if (newToken) {
            originalRequest.headers = originalRequest.headers || {}
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`
            resolve(http(originalRequest))
          } else {
            reject(error)
          }
        })
      })
    }

    if (status === 401) {
      // Clear auth state and redirect to login with feedback
      const authStore = useAuthStore()
      authStore.logout()
      snackbar.info('Session expired. Please sign in again.')
      if (router.currentRoute.value.name !== 'login') {
        router.push('/login')
      }
    }

    // Normalize error structure for consistent handling
    const normalizedError = {
      message: (error.response?.data as any)?.message || error.message || 'An unexpected error occurred',
      status: status,
      data: error.response?.data,
    }

    return Promise.reject(normalizedError)
  }
)

export default http